import React, { useCallback, useEffect, useState } from 'react'
import { Button, H2 } from '../../lib/components'
import { tradingAdminServices } from '../../services'
import { SettlementPanel } from './settlement-panel'

type Issue =
  | 'NOT_SUBMITTED'
  | 'MISSING_AT_ALPACA'
  | 'MISSING_LOCALLY'
  | 'AMOUNT_MISMATCH'
  | 'WALLET_MISMATCH'
  | 'OVERDUE'
  | 'SETTLEMENT_FAILED'
  | 'SETTLEMENT_AWAITING_FUNDS'

type ReconciliationItem = {
  fundingId: number | null
  alpacaTransferId: string | null
  investorId: number | null
  accountNo: string | null
  localStatus: string | null
  alpacaStatus: string | null
  localAmountCents: number | null
  alpacaAmountCents: number | null
  walletDebitCents: number | null
  remainingPayableCents: number | null
  totalInterestCents: number | null
  deadline: string | null
  createdAt: string | null
  settlementId: number | null
  settlementStatus: string | null
  settleable: boolean
  issues: Issue[]
}

export type Reconciliation = {
  systemDate: string
  items: ReconciliationItem[]
  totals: {
    walletDebitedCents: number
    localFundedCents: number
    alpacaFundedCents: number
    differenceCents: number
    amountOwedCents: number
    interestPenaltyCents: number
    deadline: string | null
    settleableCount: number
    settleableCents: number
  }
  issueCount: number
  balanced: boolean
}

const ISSUE_LABELS: Record<Issue, string> = {
  NOT_SUBMITTED: 'Wallet debited, not sent to Alpaca',
  MISSING_AT_ALPACA: 'Missing from Alpaca report',
  MISSING_LOCALLY: 'No Greenback record',
  AMOUNT_MISMATCH: 'Amount differs',
  WALLET_MISMATCH: 'Wallet debit differs',
  OVERDUE: 'Past settlement deadline',
  SETTLEMENT_FAILED: 'Settlement failed',
  SETTLEMENT_AWAITING_FUNDS: 'Settlement awaiting funds',
}

const settlementCell = (item: ReconciliationItem) => {
  if (item.settlementId) return `#${item.settlementId} · ${item.settlementStatus ?? '—'}`
  if (item.settleable) return 'Ready'
  return item.alpacaStatus === 'COMPLETED' || item.localStatus === 'COMPLETED' ? 'Settled' : '—'
}

const usd = (cents: number | null) =>
  cents === null
    ? '—'
    : (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' })

/** Today's date in New York, which Alpaca's instant funding system date follows. */
export const easternToday = (now: Date = new Date()) =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now)

const InstantFundingReconciliation = () => {
  const [systemDate, setSystemDate] = useState(easternToday())
  const [data, setData] = useState<Reconciliation | null>(null)
  const [onlyIssues, setOnlyIssues] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async (date: string) => {
    setLoading(true)
    setError('')
    const res = await tradingAdminServices.getReconciliation(date)
    if (res?.status === 200) {
      setData(res.data)
    } else {
      setData(null)
      setError(res?.data?.error?.message || 'Could not load the reconciliation.')
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    void load(systemDate)
  }, [load, systemDate])

  const items = data ? data.items.filter((i) => !onlyIssues || i.issues.length > 0) : []

  return (
    <section className='funding-recon'>
      <H2 text='Instant funding reconciliation' classes={['mb-24']} />
      <p className='funding-recon__intro'>
        Matches Greenback wallet debits, Greenback funding records and Alpaca&apos;s instant funding
        report for one Alpaca system date. Brokerage accounts are funded from the main account; the
        total owed must be settled with Alpaca by the deadline.
      </p>
      <div className='funding-recon__toolbar'>
        <label>
          <span>System date</span>
          <input
            type='date'
            value={systemDate}
            onChange={(e) => e.target.value && setSystemDate(e.target.value)}
          />
        </label>
        <label className='funding-recon__check'>
          <input
            type='checkbox'
            checked={onlyIssues}
            onChange={(e) => setOnlyIssues(e.target.checked)}
          />
          <span>Only show issues</span>
        </label>
        <Button
          text={loading ? 'Loading…' : 'Refresh'}
          variant='secondary-btn'
          isDisabled={loading}
          handleClick={() => void load(systemDate)}
        />
      </div>

      {error ? <p className='wallet-admin__empty'>{error}</p> : null}

      {data ? (
        <>
          <div
            role='status'
            className={`funding-recon__banner ${
              data.balanced ? 'funding-recon__banner--ok' : 'funding-recon__banner--bad'
            }`}
          >
            {data.balanced
              ? 'Balanced: the wallet, Greenback records and Alpaca agree.'
              : `${data.issueCount} transfer${data.issueCount === 1 ? '' : 's'} need attention.`}
          </div>

          <dl className='funding-recon__totals'>
            <div>
              <dt>Wallet debited</dt>
              <dd>{usd(data.totals.walletDebitedCents)}</dd>
            </div>
            <div>
              <dt>Funded (Greenback)</dt>
              <dd>{usd(data.totals.localFundedCents)}</dd>
            </div>
            <div>
              <dt>Funded (Alpaca)</dt>
              <dd>{usd(data.totals.alpacaFundedCents)}</dd>
            </div>
            <div>
              <dt>Difference</dt>
              <dd className={data.totals.differenceCents !== 0 ? 'is-bad' : undefined}>
                {usd(data.totals.differenceCents)}
              </dd>
            </div>
            <div>
              <dt>Owed to Alpaca</dt>
              <dd>{usd(data.totals.amountOwedCents)}</dd>
            </div>
            <div>
              <dt>Interest penalty</dt>
              <dd>{usd(data.totals.interestPenaltyCents)}</dd>
            </div>
            <div>
              <dt>Settle by</dt>
              <dd>{data.totals.deadline || '—'}</dd>
            </div>
          </dl>

          <SettlementPanel
            systemDate={data.systemDate}
            settleableCount={data.totals.settleableCount}
            settleableCents={data.totals.settleableCents}
            deadline={data.totals.deadline}
            onSettled={() => void load(systemDate)}
          />

          {items.length === 0 ? (
            <p className='wallet-admin__empty'>
              {onlyIssues ? 'No issues for this date.' : 'No instant funding for this date.'}
            </p>
          ) : (
            <div className='w-100 table_scroll_horizontal'>
              <table className='styled-table'>
                <thead>
                  <tr>
                    <th>Funding</th>
                    <th>Investor</th>
                    <th>Account no.</th>
                    <th>Wallet debit</th>
                    <th>Greenback amount</th>
                    <th>Alpaca amount</th>
                    <th>Status (Greenback / Alpaca)</th>
                    <th>Remaining payable</th>
                    <th>Settlement</th>
                    <th>Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr
                      key={item.fundingId ?? item.alpacaTransferId}
                      className={item.issues.length ? 'funding-recon__row--bad' : undefined}
                    >
                      <td>
                        {item.fundingId ? `#${item.fundingId}` : '—'}
                        {item.alpacaTransferId ? <small>{item.alpacaTransferId}</small> : null}
                      </td>
                      <td>{item.investorId ?? '—'}</td>
                      <td>{item.accountNo || '—'}</td>
                      <td>{usd(item.walletDebitCents)}</td>
                      <td>{usd(item.localAmountCents)}</td>
                      <td>{usd(item.alpacaAmountCents)}</td>
                      <td>
                        {item.localStatus || '—'} / {item.alpacaStatus || '—'}
                      </td>
                      <td>{usd(item.remainingPayableCents)}</td>
                      <td>{settlementCell(item)}</td>
                      <td>
                        {item.issues.length === 0 ? (
                          'OK'
                        ) : (
                          <ul className='funding-recon__issues'>
                            {item.issues.map((issue) => (
                              <li key={issue}>{ISSUE_LABELS[issue] ?? issue}</li>
                            ))}
                          </ul>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}
    </section>
  )
}

export { InstantFundingReconciliation }
